import isEqual from "lodash/isEqual";

import { normalizeMaritimeWidgetFormValues } from "@/lib/api/maritime-widget-configuration.utils";
import {
  normalizeModelMapWidgetFormValues,
  normalizeObservationsMapWidgetFormValues,
} from "@/lib/api/observations-map-widget-configuration.utils";
import {
  normalizeTimeSeriesWidgetFormValues,
  readTimeSeriesConfiguration,
} from "@/lib/api/time-series-widget-configuration.utils";
import type { Widget } from "@/lib/api/types";
import { normalizeBaseLayerStyle } from "@/lib/mapbox/base-layer-styles";

/**
 * True when an update submit would persist the same widget as already stored.
 * Applies the same CMS save normalizers as the type-specific dialogs so
 * shape-only diffs (maritime area-id cleanup, time-series `unit` → `unit_code`)
 * do not force an update request.
 */
export function isUnchangedWidgetUpdate(
  initial: Partial<Widget>,
  submitted: Widget,
): boolean {
  return isEqual(
    canonicalizeWidgetUpdatePayload(submitted),
    canonicalizeWidgetUpdatePayload(initial as Widget),
  );
}

/** Same CMS save normalizers the type-specific dialogs apply on submit. */
export function canonicalizeWidgetUpdatePayload(values: Widget): Widget {
  const type = values.configuration?.type;
  if (type === "observations_map") {
    return normalizeObservationsMapWidgetFormValues(normalizeBaseLayerStyle(values));
  }
  if (type === "model_map") {
    return normalizeModelMapWidgetFormValues(normalizeBaseLayerStyle(values));
  }
  if (type === "time_series") {
    const chart = readTimeSeriesConfiguration(values.configuration);
    if (chart?.unit?.code) {
      return normalizeTimeSeriesWidgetFormValues(values);
    }
    return values;
  }
  return normalizeMaritimeWidgetFormValues(values);
}
